import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    ISimpleControllerChildControllerConfiguration, ISimpleControllerConfiguration, SimpleBaseController, SoundType,
} from '@denishussar/html5-game-engine';
import { SpineAnimationBaseController } from '../../Spine/SpineAnimationBaseController';
import { FuelModel } from '../../../Models/MiniGames/FuelModel';
import { FuelLogic } from '../../../Logic/MiniGames/FuelLogic';
import { Container, Graphics } from 'pixi.js';
import { GameLogic } from '../../../Logic/GameLogic';
import { DialogTextEnum, DialogTypeTextEnum } from '../../../Models/GameModel';
import { SoundConstants, SoundLevels } from '../../../Constants/SoundConstants';

export interface IFuelGameControllerOptions {
    onComplete: () => void;
}

export class FuelGameController extends SimpleBaseController {
    public $model: FuelModel;
    private _width: number = 0;
    private _height: number = 0;

    private _backgroundShape: Graphics;

    private _mainAnimation: SpineAnimationBaseController;
    private _fillAnimation: SpineAnimationBaseController;

    private _phialHideToAnimateSprites = [
        'phial',
        'phial_back',
        'phial_bottom',
        'shadowPhial',
    ];

    private _flaskCount: number = 5;
    private _flaskHideToAnimateSprites = [
        /*
        'phial',
        'phial_back',
        'phial_bottom',
        */
        'shadow',
        'H2O',
        'flask',
        'flask_back',
        'ttl_Ca',
        'ttl_H2O',
        'ttl_HCl',
        'ttl_Na',
        'ttl_Zn',
    ];

    private _flaskNameList: string[] = ['flask', 'flask2', 'flask3', 'flask4', 'flask5'];
    private _flaskCombination: string = '';
    private _flaskCombinations: string[] = ['35', '53', '42', '24', '12', '21'];

    public constructor(
        @Inject private _options: IFuelGameControllerOptions,
        @Inject private _fuelLogic: FuelLogic,
        @Inject private _gameLogic: GameLogic,
    ) {
        super('FuelGameController');
    }

    protected getConfiguration(): ISimpleControllerConfiguration {
        const flaskControllers = {};

        for (let i = 1; i <= this._flaskCount; i++) {
            flaskControllers[`flaskAnimation${i}`] = {
                controller: SpineAnimationBaseController,
                model: this.$model,
                options: {
                    key: `fuel_flask`,
                    url: `/images/spine/mini_game_fuel/minigame_fuel_3200.json`,
                    animations: [
                        `flask_${i}_choice`,
                    ],
                },
            };
        }

        return {
            sounds:[
                {
                    id: SoundConstants.ECO_GLASS,
                    type: SoundType.SFX,
                },
                {
                    id: SoundConstants.ECO_REAG_USE,
                    type: SoundType.SFX,
                },
                {
                    id: SoundConstants.ECO_BANG,
                    type: SoundType.SFX,
                },
                {
                    id: SoundConstants.ECO_RIGHT_ANWSER,
                    type: SoundType.SFX,
                },
            ],
            childControllers: {

                mainAnimation: {
                    controller: SpineAnimationBaseController,
                    model: this.$model,
                    options: {
                        key: `fuel_main`,
                        url: `/images/spine/mini_game_fuel/minigame_fuel_3200.json`,
                        animations: [
                            'start_screen',
                            'game_lose',
                            'game_win',
                        ],
                    },
                },
                fillAnimation: {
                    controller: SpineAnimationBaseController,
                    model: this.$model,
                    options: {
                        key: `fuel_fill`,
                        url: `/images/spine/mini_game_fuel/minigame_fuel_3200.json`,
                        animations: [
                            'liquid_1_fill_start',
                            'liquid_1_fill_end',
                            'liquid_2_fill_start',
                            'liquid_2_fill_end',
                            'liquid_3_fill_start',
                            'liquid_3_fill_end',
                            'liquid_4_fill_start',
                            'liquid_4_fill_end',
                            'liquid_5_fill_start',
                            'liquid_5_fill_end',
                        ],
                    },
                },
                ...flaskControllers,
            },
        };
    }

    protected onInitialize(): void {
        this.display.interactive = true;

        this._mainAnimation = this.$childControllers.get('mainAnimation');
        this._mainAnimation.resetAnimation();

        this._fillAnimation = this.$childControllers.get('fillAnimation');
        this._fillAnimation.resetAnimation();

        this._mainAnimation.setAnimation('start_screen', false);

        this._flaskNameList.forEach((name, index) => {
            this.makeFlaskInteractive(name, this.onFlaskMouseDown.bind(this, index + 1));
        });

        this._width = 1920;
        this._height = 1440;

        this._backgroundShape = new Graphics();
        this.display.addChildAt(this._backgroundShape, 0);

        this._gameLogic.showDialogScreen(DialogTextEnum.fuel, DialogTypeTextEnum.start);
    }

    protected onResize(width: number, height: number, scale: number) {
        const scaleValue = Math.min(
            width / scale / this._width,
            height / scale / this._height,
        );
        this.scaleX = scaleValue;
        this.scaleY = scaleValue;
        // this.button.x = width/2 - this.button.width/2;
        // this.button.y = -height/2 + this.button.height/2;
        if (this._backgroundShape) {
            const w = width * 10;
            const h = height * 10;

            this._backgroundShape.clear();
            this._backgroundShape.beginFill(0x3A6473);
            this._backgroundShape.drawRect(-w / 2, -h / 2, w, h);
            this._backgroundShape.endFill();
        }
    }

    private getSlotSprite(spineController: SpineAnimationBaseController, name: string): PIXI.spine.SpineSprite {
        const slot = spineController.getSpine().skeleton.findSlot(name);
        if (!slot) {
            return null;
        }

        const sprite = slot.currentSprite;
        if (!sprite) {
            return null;
        }

        return sprite;
    }

    private getSlotMesh(spineController: SpineAnimationBaseController, name: string): PIXI.spine.SpineMesh {
        const slot = spineController.getSpine().skeleton.findSlot(name);
        if (!slot) {
            return null;
        }

        const mesh = slot.currentMesh;
        if (!mesh) {
            return null;
        }

        return mesh;
    }

    private makeFlaskInteractive(name: string, onPointerDown: (index: number) => void): void {
        const sprite = this.getSlotSprite(this._mainAnimation, name);
        if (!sprite) {
            return;
        }

        sprite.buttonMode = true;
        sprite.interactive = true;
        sprite.on('pointerdown', onPointerDown);
    }

    private setFlaskInteractive(index: number, interactive: boolean): void {
        const sprite = this.getSlotSprite(this._mainAnimation, this._flaskNameList[index - 1]);
        if (!sprite) {
            return;
        }

        sprite.buttonMode = interactive;
        sprite.interactive = interactive;
    }

    private setFlaskAllInteractive(interactive: boolean): void {
        for (let i = 1; i <= this._flaskCount; i++) {
            this.setFlaskInteractive(i, interactive);
        }
    }

    private setSpineSpritesVisibility(names: string[], spineController: SpineAnimationBaseController, visibility: boolean): void {
        names.forEach(name => {
            const sprite = this.getSlotSprite(spineController, name);
            const mesh = this.getSlotMesh(spineController, name);

            if (sprite) {
                sprite.visible = visibility;
            }
            if (mesh) {
                mesh.visible = visibility;
            }
        });
    }

    private setFlaskSpritesVisibility(names: string[], spineController: SpineAnimationBaseController, index: number, visibility: boolean): void {
        this.setSpineSpritesVisibility(names.map(name => index > 1 ? name + index : name), spineController, visibility);
    }

    private getFlaskAnimation(index: number): SpineAnimationBaseController {
        return this.$childControllers.get(`flaskAnimation${index}`);
    }

    private hideFlasks(): void {
        for (let i = 1; i <= this._flaskCount; i++) {
            this.setFlaskSpritesVisibility(this._flaskHideToAnimateSprites, this._mainAnimation, i, false);
            this.getFlaskAnimation(i).resetAnimation();
        }
    }

    private showFlasks(): void {
        for (let i = 1; i <= this._flaskCount; i++) {
            this.setFlaskSpritesVisibility(this._flaskHideToAnimateSprites, this._mainAnimation, i, true);
            this.getFlaskAnimation(i).resetAnimation();
        }
    }

    private onFlaskMouseDown(index: number): void {
        this.setFlaskAllInteractive(false);
        this.soundManager.playSound(SoundConstants.ECO_GLASS,0, SoundLevels.MINIGAME_ACTION);

        const timeout = 3000;
        const fillAnimation = this._flaskCombination.length === 0 ? 'start' : 'end';

        this.getFlaskAnimation(index).setAnimation(`flask_${index}_choice`, false);
        this._fillAnimation.setAnimation(`liquid_${index}_fill_${fillAnimation}`, false);

        this.setSpineSpritesVisibility(this._phialHideToAnimateSprites, this._mainAnimation, false);
        this.setFlaskSpritesVisibility(this._flaskHideToAnimateSprites, this._mainAnimation, index, false);

        setTimeout(()=>{
            // sound manager delay doesn't work
            this.soundManager.playSound(SoundConstants.ECO_REAG_USE, 0, SoundLevels.MINIGAME_ACTION);
        }, 600);
        setTimeout(() => {
            this._flaskCombination += index.toString();

            if (this._flaskCombination.length === this._flaskCombinations[0].length) {
                this.hideFlasks();
                this.setSpineSpritesVisibility(this._phialHideToAnimateSprites, this._mainAnimation, true);

                this._fillAnimation.resetAnimation();

                if (this._flaskCombinations.indexOf(this._flaskCombination) > -1) {
                    this._mainAnimation.setAnimation('game_win', false);
                    this.soundManager.playSound(SoundConstants.ECO_RIGHT_ANWSER, 0, SoundLevels.MINIGAME_ACTION);
                    setTimeout(() => {
                        this._options.onComplete();
                        this._gameLogic.showDialogScreen(DialogTextEnum.fuel, DialogTypeTextEnum.end);
                    }, timeout);

                } else {
                    this._mainAnimation.setAnimation('game_lose', false);
                    this.soundManager.playSound(SoundConstants.ECO_BANG, 0, SoundLevels.MINIGAME_ACTION);
                    this._flaskCombination = '';

                    setTimeout(() => {
                        this._gameLogic.showDialogScreen(DialogTextEnum.fuel, DialogTypeTextEnum.hint);

                        this._mainAnimation.setAnimation('start_screen', false);
                        this._fillAnimation.resetAnimation();

                        this.setSpineSpritesVisibility(this._phialHideToAnimateSprites, this._mainAnimation, true);
                        this.setFlaskAllInteractive(true);
                        this.showFlasks();

                    }, timeout * 1.25);
                }
            } else {
                this.setFlaskAllInteractive(true);
            }
        }, timeout);
    }
}
